import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Input, Select, message, Tabs, Modal, Tag, Segmented, Tooltip, Empty } from 'antd';
import { EditOutlined, DeleteOutlined, SendOutlined, EyeOutlined, PauseOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
// Admin 패널은 다국어 지원 불필요
import adminInAppMessagingService from '../services/adminInAppMessagingService';
import { messageCategories, targetingOptions, priorityOptions, messageTypeOptions } from '../data/myanmarMessageTemplates';
import { getKoreanContent, messageTemplatesKo } from '../data/messageTemplatesKo';

const { TextArea } = Input;
const { Option } = Select;

const CATEGORY_NAME_KO = {
  consultation: '전문가 상담',
  community: '커뮤니티 센터',
  news: '뉴스',
  remittance: '송금 서비스',
  myanmarStory: '미얀마 이야기',
  messaging: '메시지',
  encouragement: '응원 메시지',
  urgent: '긴급 공지',
  kyc: 'KYC 인증',
  embassy: '대사관 서비스',
  education: '교육 서비스',
  lifestyle: '생활 정보',
  promotion: '프로모션·행사',
  policy_compliance: '정책 준수 관리',
};

const TYPE_TAG_COLOR = { success: 'green', warning: 'orange', error: 'red', info: 'blue' };
const TYPE_ICON = { success: '✓', warning: '!', error: '✕', info: 'i' };

const STATUS_META = {
  active: { color: 'green', label: 'Active' },
  paused: { color: 'orange', label: 'Paused' },
  draft: { color: 'blue', label: 'Draft' },
  completed: { color: 'default', label: 'Completed' },
};

const LANG_OPTIONS = [
  { label: '함께 보기', value: 'both' },
  { label: '한국어', value: 'ko' },
  { label: 'မြန်မာ', value: 'my' },
];

const InAppMessagingManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [previewModal, setPreviewModal] = useState(false);
  const [previewMessage, setPreviewMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  const [editModal, setEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editForm] = Form.useForm();
  const [targetingModal, setTargetingModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState('all_users');
  const [langMode, setLangMode] = useState('both');
  const [templateSearch, setTemplateSearch] = useState('');

  const templateById = useMemo(() => {
    const map = {};
    Object.values(messageCategories).forEach((category) => {
      Object.values(category.templates || {}).forEach((template) => {
        map[template.id] = template;
      });
    });
    return map;
  }, []);

  const resolveKorean = (item) => getKoreanContent(item, item?.templateId ? templateById[item.templateId] : null);

  // 메시지 목록 조회
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const result = await adminInAppMessagingService.getMessages();
      if (result.success) {
        setMessages(result.data);
      } else {
        message.error('메시지 목록을 불러오는데 실패했습니다.');
      }
    } catch (error) {
      message.error('메시지 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  // 템플릿에서 메시지 생성 (타겟팅 선택)
  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setSelectedTarget(template.targetUsers || 'all_users');
    setTargetingModal(true);
  };

  // 미얀마어 버튼 텍스트를 영어로 변환
  const translateActionToEnglish = (myanmarAction) => {
    const translations = {
      'ဆွေးနွေးမှု စတင်ပါ': 'Start Consultation',
      'အဖြေကို ကြည့်ရှုပါ': 'View Answer',
      'ကျန်းမာရေး ဆွေးနွေးမှု စတင်ပါ': 'Start Health Consultation',
      'အလုပ်နှင့် ပတ်သက်သော ဆွေးနွေးမှု စတင်ပါ': 'Start Work Consultation',
      'ပို့စ်များ ကြည့်ရှုပါ': 'View Posts',
      'အလုပ်အခွင့်အလမ်းများ ကြည့်ရှုပါ': 'View Job Opportunities',
      'နေအိမ် ရှာဖွေမှု ကြည့်ရှုပါ': 'View Housing Search',
      'ပွဲတော်များ ကြည့်ရှုပါ': 'View Events',
      'အကူအညီ ရှာဖွေမှု ကြည့်ရှုပါ': 'View Help Requests',
      'အသေးစိတ် ကြည့်ရှုပါ': 'View Details',
      'အသုံးပြုပါ': 'Use Service',
      'ကြည့်ရှုပါ': 'View',
      'စတင်ပါ': 'Start'
    };
    return translations[myanmarAction] || myanmarAction;
  };

  // 타겟팅 선택 후 메시지 생성
  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const customizations = {
        targetUsers: selectedTarget,
        action: selectedTemplate.action ? translateActionToEnglish(selectedTemplate.action) : selectedTemplate.action
      };

      const result = await adminInAppMessagingService.createMessageFromTemplate(selectedTemplate.id, customizations);
      if (result.success) {
        message.success('메시지가 생성되었습니다.');
        fetchMessages();
        setActiveTab('messages');
        setTargetingModal(false);
        setSelectedTemplate(null);
      } else {
        message.error('메시지 생성에 실패했습니다.');
      }
    } catch (error) {
      message.error('메시지 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 새 메시지 생성
  const handleCreateMessage = async (values) => {
    setLoading(true);
    try {
      const result = await adminInAppMessagingService.createMessage(values);
      if (result.success) {
        message.success('메시지가 생성되었습니다.');
        form.resetFields();
        fetchMessages();
        setActiveTab('messages');
      } else {
        message.error('메시지 생성에 실패했습니다.');
      }
    } catch (error) {
      message.error('메시지 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 메시지 발송
  const handleSendMessage = async (messageId) => {
    try {
      const result = await adminInAppMessagingService.sendMessage(messageId);
      if (result.success) {
        message.success('메시지가 발송되었습니다.');
        fetchMessages();
      } else {
        message.error('메시지 발송에 실패했습니다.');
      }
    } catch (error) {
      message.error('메시지 발송에 실패했습니다.');
    }
  };

  // 메시지 상태 변경
  const handleToggleMessage = async (messageId, status) => {
    try {
      const result = status === 'active'
        ? await adminInAppMessagingService.resumeMessage(messageId)
        : await adminInAppMessagingService.pauseMessage(messageId);

      if (result.success) {
        message.success(`메시지가 ${status === 'active' ? '활성화' : '비활성화'}되었습니다.`);
        fetchMessages();
      } else {
        message.error('메시지 상태 변경에 실패했습니다.');
      }
    } catch (error) {
      message.error('메시지 상태 변경에 실패했습니다.');
    }
  };

  // 메시지 삭제
  const handleDeleteMessage = async (messageId) => {
    Modal.confirm({
      title: '메시지 삭제',
      content: '정말로 이 메시지를 삭제하시겠습니까?',
      okText: '삭제',
      cancelText: '취소',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await adminInAppMessagingService.deleteMessage(messageId);
          if (result.success) {
            message.success('메시지가 삭제되었습니다.');
            fetchMessages();
          } else {
            message.error('메시지 삭제에 실패했습니다.');
          }
        } catch (error) {
          message.error('메시지 삭제에 실패했습니다.');
        }
      }
    });
  };

  // 메시지 미리보기
  const handlePreviewMessage = (messageData) => {
    setPreviewMessage(messageData);
    setPreviewModal(true);
  };

  // 템플릿 편집
  const handleEditTemplate = (template) => {
    const ko = messageTemplatesKo[template.id] || {};
    setEditingTemplate(template);
    editForm.setFieldsValue({
      title: template.title,
      body: template.body,
      action: template.action,
      actionUrl: template.actionUrl,
      type: template.type,
      targetUsers: template.targetUsers,
      priority: template.priority,
      titleKo: ko.title || '',
      bodyKo: ko.body || '',
      actionKo: ko.action || '',
    });
    setEditModal(true);
  };

  // 템플릿 수정 저장
  const handleSaveTemplateEdit = async (values) => {
    try {
      const result = await adminInAppMessagingService.createMessageFromTemplate(editingTemplate.id, values);
      if (result.success) {
        message.success('수정한 템플릿으로 메시지를 만들었습니다.');
        setEditModal(false);
        setEditingTemplate(null);
        editForm.resetFields();
        fetchMessages();
        setActiveTab('messages');
      } else {
        message.error('메시지 생성에 실패했습니다.');
      }
    } catch (error) {
      message.error('메시지 생성에 실패했습니다.');
    }
  };

  const stats = useMemo(() => {
    const active = messages.filter((m) => m.status === 'active').length;
    const sent = messages.reduce((sum, m) => sum + (m.sentCount || 0), 0);
    const avg = (key) =>
      messages.length > 0 ? (messages.reduce((sum, m) => sum + (m[key] || 0), 0) / messages.length).toFixed(1) : '0';
    return [
      { key: 'active', label: 'Active', value: active, cls: 'bg-emerald-50 text-emerald-600', icon: '📢' },
      { key: 'sent', label: 'Sent', value: sent.toLocaleString(), cls: 'bg-blue-50 text-blue-600', icon: '📤' },
      { key: 'click', label: 'Click Rate', value: `${avg('clickRate')}%`, cls: 'bg-amber-50 text-amber-600', icon: '👆' },
      { key: 'conv', label: 'Conversion', value: `${avg('conversionRate')}%`, cls: 'bg-violet-50 text-violet-600', icon: '🎯' },
    ];
  }, [messages]);

  // 미얀마어/한국어 텍스트 표시 (langMode 기준)
  const BilingualText = ({ item, field, clamp = '', myClass = '', koClass = '' }) => {
    const ko = resolveKorean(item);
    const myText = item?.[field] || '';
    const koText = ko?.[field] || '';
    if (langMode === 'my') return <div className={`${clamp} ${myClass}`}>{myText}</div>;
    if (langMode === 'ko') {
      return koText
        ? <div className={`${clamp} ${myClass}`}>{koText}</div>
        : (
          <div className={`${clamp} ${myClass}`}>
            {myText}
            <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] font-normal text-slate-400">번역 없음</span>
          </div>
        );
    }
    return (
      <>
        <div className={`${clamp} ${myClass}`}>{myText}</div>
        {koText && <div className={`${clamp} mt-0.5 text-slate-500 ${koClass}`}>🇰🇷 {koText}</div>}
      </>
    );
  };

  const filteredCategories = useMemo(() => {
    const term = templateSearch.trim().toLowerCase();
    return Object.entries(messageCategories)
      .map(([categoryKey, category]) => {
        const templates = Object.values(category.templates || {}).filter((template) => {
          if (!term) return true;
          const ko = messageTemplatesKo[template.id] || {};
          return [template.title, template.body, ko.title, ko.body, category.name, CATEGORY_NAME_KO[categoryKey]]
            .filter(Boolean)
            .some((text) => text.toLowerCase().includes(term));
        });
        return { categoryKey, category, templates };
      })
      .filter(({ templates }) => templates.length > 0);
  }, [templateSearch]);

  const koreanFields = (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/70 p-3">
      <div className="mb-2 text-xs font-semibold text-slate-600">
        🇰🇷 한국어 (관리자 확인용 · 선택) <span className="font-normal text-slate-400">— 사용자에게는 미얀마어로 표시됩니다</span>
      </div>
      <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
        <Form.Item name="titleKo" label="제목 (한국어)">
          <Input placeholder="예: 💰 송금 서비스" />
        </Form.Item>
        <Form.Item name="actionKo" label="버튼 텍스트 (한국어)">
          <Input placeholder="예: 송금하기" />
        </Form.Item>
      </div>
      <Form.Item name="bodyKo" label="내용 (한국어)" className="mb-0">
        <TextArea rows={2} placeholder="예: 가족에게 안전하게 송금하세요." />
      </Form.Item>
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input.Search
          allowClear
          placeholder="템플릿 검색 (미얀마어·한국어)"
          value={templateSearch}
          onChange={(e) => setTemplateSearch(e.target.value)}
          className="max-w-xs"
          size="middle"
        />
        <span className="text-xs text-slate-500">
          {filteredCategories.reduce((sum, c) => sum + c.templates.length, 0)} templates
        </span>
      </div>

      {filteredCategories.length === 0 ? (
        <Empty description="검색 결과가 없습니다" className="py-8" />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          {filteredCategories.map(({ categoryKey, category, templates }) => (
            <div key={categoryKey} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">{category.icon}</span>
                  <span className="text-[13px] font-semibold text-slate-800">{CATEGORY_NAME_KO[categoryKey] || category.name}</span>
                  <span className="text-[11px] text-slate-400">{category.name}</span>
                </div>
                <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-semibold text-slate-600">{templates.length}</span>
              </div>

              <div className="divide-y divide-slate-100">
                {templates.map((template) => (
                  <div key={template.id} className="group px-3 py-2 hover:bg-slate-50/70">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <BilingualText
                          item={template}
                          field="title"
                          clamp="line-clamp-1"
                          myClass="text-[13px] font-semibold text-slate-900"
                          koClass="text-xs font-medium"
                        />
                        <div className="mt-0.5">
                          <BilingualText
                            item={template}
                            field="body"
                            clamp="line-clamp-2"
                            myClass="text-xs text-slate-600"
                            koClass="text-xs"
                          />
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <Tag color={TYPE_TAG_COLOR[template.type] || 'blue'} className="m-0 text-[10px] leading-4">{template.type}</Tag>
                          <Tag className="m-0 text-[10px] leading-4">{template.priority}</Tag>
                          <span className="text-[11px] text-slate-400">
                            → {targetingOptions[template.targetUsers] || template.targetUsers}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Tooltip title="미리보기">
                          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => handlePreviewMessage(template)} />
                        </Tooltip>
                        <Tooltip title="수정 후 사용">
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEditTemplate(template)} />
                        </Tooltip>
                        <Button size="small" type="primary" icon={<SendOutlined />} onClick={() => handleUseTemplate(template)}>
                          Use
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderMessagesTab = () => (
    <div className="p-3">
      {messages.length === 0 ? (
        <Empty description="생성된 메시지가 없습니다" className="py-8" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-left">
            <thead className="bg-slate-50">
              <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Message</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Performance</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {messages.map((msg, index) => {
                const status = STATUS_META[msg.status] || { color: 'default', label: msg.status };
                return (
                  <tr key={msg.id || `message-${index}`} className="align-top hover:bg-slate-50/70">
                    <td className="max-w-md px-3 py-2">
                      <BilingualText item={msg} field="title" clamp="line-clamp-1" myClass="text-[13px] font-medium text-slate-900" koClass="text-xs" />
                      <div className="mt-0.5">
                        <BilingualText item={msg} field="body" clamp="line-clamp-1" myClass="text-xs text-slate-500" koClass="text-xs" />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Tag color={status.color} className="m-0 text-[11px]">{status.label}</Tag>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {targetingOptions[msg.targetUsers] || msg.targetUsers}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium text-slate-800">{msg.sentCount || 0} sent</div>
                      <div className="text-slate-400">{msg.clickRate || 0}% click</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                      {msg.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip title="미리보기">
                          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => handlePreviewMessage(msg)} />
                        </Tooltip>
                        {msg.status === 'draft' && (
                          <Button size="small" type="primary" icon={<SendOutlined />} onClick={() => handleSendMessage(msg.id)}>
                            Send
                          </Button>
                        )}
                        {msg.status === 'active' && (
                          <Tooltip title="일시정지">
                            <Button size="small" icon={<PauseOutlined />} onClick={() => handleToggleMessage(msg.id, 'paused')} />
                          </Tooltip>
                        )}
                        {msg.status === 'paused' && (
                          <Tooltip title="재개">
                            <Button size="small" icon={<PlayCircleOutlined />} onClick={() => handleToggleMessage(msg.id, 'active')} />
                          </Tooltip>
                        )}
                        <Tooltip title="삭제">
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteMessage(msg.id)} />
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderCreateTab = () => (
    <div className="p-3">
      <Form form={form} layout="vertical" onFinish={handleCreateMessage} className="compact-inapp-form max-w-4xl">
        <div className="grid grid-cols-1 gap-x-3 lg:grid-cols-2">
          <Form.Item
            name="title"
            label={<span>제목 <span className="text-slate-400">(미얀마어)</span></span>}
            rules={[{ required: true, message: 'Please enter message title.' }]}
          >
            <Input placeholder="e.g.: 💰 ငွေလွှဲပြောင်းမှု ဝန်ဆောင်မှု" />
          </Form.Item>
          <Form.Item name="type" label="Message Type" rules={[{ required: true, message: 'Please select message type.' }]}>
            <Select placeholder="Select message type">
              {Object.entries(messageTypeOptions).map(([key, value]) => (
                <Option key={key} value={key}>{value}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          name="body"
          label={<span>내용 <span className="text-slate-400">(미얀마어)</span></span>}
          rules={[{ required: true, message: 'Please enter message content.' }]}
        >
          <TextArea rows={3} placeholder="e.g.: မိသားစုများထံ ဘေးကင်းစွာ ငွေလွှဲပြောင်းပါ။" />
        </Form.Item>

        <div className="grid grid-cols-1 gap-x-3 lg:grid-cols-2">
          <Form.Item name="action" label={<span>버튼 텍스트 <span className="text-slate-400">(미얀마어)</span></span>}>
            <Input placeholder="e.g.: ငွေလွှဲပြောင်းပါ" />
          </Form.Item>
          <Form.Item name="actionUrl" label="Action URL">
            <Input placeholder="e.g.: /remittance" />
          </Form.Item>
        </div>

        <div className="mb-3">{koreanFields}</div>

        <div className="grid grid-cols-1 gap-x-3 lg:grid-cols-2">
          <Form.Item name="targetUsers" label="Target Audience" rules={[{ required: true, message: 'Please select targeting.' }]}>
            <Select placeholder="Select target audience" showSearch optionFilterProp="children">
              {Object.entries(targetingOptions).map(([key, value]) => (
                <Option key={key} value={key}>{value}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{ required: true, message: 'Please select priority.' }]}>
            <Select placeholder="Select priority">
              {Object.entries(priorityOptions).map(([key, value]) => (
                <Option key={key} value={key}>{value}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Button onClick={() => form.resetFields()}>Reset</Button>
          <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />} className="bg-slate-900 hover:!bg-slate-800">
            Create Message
          </Button>
        </div>
      </Form>
    </div>
  );

  const previewKo = previewMessage ? resolveKorean(previewMessage) : null;

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <style>{`
        .inapp-tabs .ant-tabs-nav { margin-bottom: 0; }
        .inapp-tabs .ant-tabs-tab { padding: 8px 0; font-size: 13px; }
        .compact-inapp-form .ant-form-item { margin-bottom: 12px; }
        .compact-inapp-form .ant-form-item-label { padding-bottom: 4px; }
        .compact-inapp-form .ant-form-item-label > label { font-size: 12px; font-weight: 600; color: #334155; }
      `}</style>

      {/* Header + Stats */}
      <div className="mb-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-sm text-white">💬</div>
            <div>
              <h1 className="text-[15px] font-semibold leading-tight text-slate-900">In-App Messaging Management</h1>
              <p className="text-[11px] leading-tight text-slate-500">Communication platform for Myanmar workers</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {stats.map((stat) => (
              <div key={stat.key} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                <span className={`flex h-6 w-6 items-center justify-center rounded-md text-xs ${stat.cls}`}>{stat.icon}</span>
                <span>
                  <span className="block text-[10px] leading-tight text-slate-500">{stat.label}</span>
                  <span className="block text-sm font-bold leading-tight text-slate-900 tabular-nums">{stat.value}</span>
                </span>
              </div>
            ))}
            <Tooltip title="새로고침">
              <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={fetchMessages} />
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="inapp-tabs"
          tabBarStyle={{ padding: '0 16px', margin: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
          tabBarExtraContent={
            <div className="flex items-center gap-2">
              <span className="hidden text-[11px] text-slate-500 sm:inline">메시지 언어</span>
              <Segmented size="small" value={langMode} onChange={setLangMode} options={LANG_OPTIONS} />
            </div>
          }
          items={[
            { key: 'templates', label: <span>📋 Message Templates</span>, children: renderTemplatesTab() },
            {
              key: 'messages',
              label: (
                <span className="flex items-center gap-1.5">
                  📨 Messages
                  <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-semibold text-slate-600">{messages.length}</span>
                </span>
              ),
              children: renderMessagesTab(),
            },
            { key: 'create', label: <span>➕ Create New</span>, children: renderCreateTab() },
          ]}
        />
      </div>

      {/* Message Preview Modal */}
      <Modal
        title="Message Preview"
        open={previewModal}
        onCancel={() => setPreviewModal(false)}
        footer={[<Button key="close" onClick={() => setPreviewModal(false)}>Close</Button>]}
        width={460}
      >
        {previewMessage && (
          <div className="space-y-3">
            {[
              { lang: 'my', flag: '🇲🇲', label: '미얀마어 (사용자 화면)', content: previewMessage, later: 'နောက်မှ' },
              { lang: 'ko', flag: '🇰🇷', label: '한국어 번역', content: previewKo, later: '나중에' },
            ].map(({ lang, flag, label, content, later }) => (
              <div key={lang}>
                <div className="mb-1 text-[11px] font-semibold text-slate-500">{flag} {label}</div>
                {content ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="flex items-start gap-2.5">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                        {TYPE_ICON[previewMessage.type] || 'i'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900">{content.title}</div>
                        <p className="mt-0.5 text-[13px] text-slate-700">{content.body}</p>
                        {(content.action || previewMessage.action) && (
                          <div className="mt-2 flex gap-2">
                            <span className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                              {content.action || previewMessage.action}
                            </span>
                            <span className="rounded-md bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">{later}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-400">
                    한국어 번역이 없습니다. 직접 작성한 메시지는 만들 때 한국어 칸을 채우면 여기에 표시됩니다.
                  </div>
                )}
              </div>
            ))}
            <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
              <div><div className="text-[10px] text-slate-400">Target</div>{targetingOptions[previewMessage.targetUsers] || previewMessage.targetUsers}</div>
              <div><div className="text-[10px] text-slate-400">Priority</div>{priorityOptions[previewMessage.priority] || previewMessage.priority}</div>
              <div><div className="text-[10px] text-slate-400">Type</div>{messageTypeOptions[previewMessage.type] || previewMessage.type}</div>
            </div>
          </div>
        )}
      </Modal>

      {/* Template Edit Modal */}
      <Modal
        title="템플릿 수정 후 메시지 만들기"
        open={editModal}
        onCancel={() => {
          setEditModal(false);
          setEditingTemplate(null);
          editForm.resetFields();
        }}
        footer={null}
        width={680}
      >
        {editingTemplate && (
          <Form form={editForm} layout="vertical" onFinish={handleSaveTemplateEdit} className="compact-inapp-form">
            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
              <Form.Item name="title" label="제목 (미얀마어)" rules={[{ required: true, message: 'Please enter message title.' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="type" label="Message Type" rules={[{ required: true, message: 'Please select message type.' }]}>
                <Select>
                  {Object.entries(messageTypeOptions).map(([key, value]) => (
                    <Option key={key} value={key}>{value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <Form.Item name="body" label="내용 (미얀마어)" rules={[{ required: true, message: 'Please enter message content.' }]}>
              <TextArea rows={3} />
            </Form.Item>
            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
              <Form.Item name="action" label="버튼 텍스트 (미얀마어)">
                <Input />
              </Form.Item>
              <Form.Item name="actionUrl" label="Action URL">
                <Input placeholder="e.g.: /remittance" />
              </Form.Item>
            </div>

            <div className="mb-3">{koreanFields}</div>

            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
              <Form.Item name="targetUsers" label="Target Audience" rules={[{ required: true, message: 'Please select targeting.' }]}>
                <Select showSearch optionFilterProp="children">
                  {Object.entries(targetingOptions).map(([key, value]) => (
                    <Option key={key} value={key}>{value}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="priority" label="Priority" rules={[{ required: true, message: 'Please select priority.' }]}>
                <Select>
                  {Object.entries(priorityOptions).map(([key, value]) => (
                    <Option key={key} value={key}>{value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
              <Button
                onClick={() => {
                  setEditModal(false);
                  setEditingTemplate(null);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
                Create Message
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      {/* Targeting Selection Modal */}
      <Modal
        title="Select Target Audience"
        open={targetingModal}
        onCancel={() => {
          setTargetingModal(false);
          setSelectedTemplate(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setTargetingModal(false);
              setSelectedTemplate(null);
            }}
          >
            Cancel
          </Button>,
          <Button key="create" type="primary" onClick={handleCreateFromTemplate} loading={loading}>
            Create Message
          </Button>
        ]}
        width={520}
      >
        {selectedTemplate && (
          <div className="space-y-3">
            <div className="rounded-lg bg-blue-50 p-3">
              <div className="text-sm font-semibold text-slate-900">{selectedTemplate.title}</div>
              <p className="mt-0.5 text-[13px] text-slate-600">{selectedTemplate.body}</p>
              {messageTemplatesKo[selectedTemplate.id] && (
                <div className="mt-2 border-t border-blue-100 pt-2 text-xs text-slate-500">
                  <div className="font-medium text-slate-700">🇰🇷 {messageTemplatesKo[selectedTemplate.id].title}</div>
                  <div className="mt-0.5">{messageTemplatesKo[selectedTemplate.id].body}</div>
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Target Audience</label>
              <Select
                value={selectedTarget}
                onChange={setSelectedTarget}
                className="w-full"
                showSearch
                optionFilterProp="children"
              >
                {Object.entries(targetingOptions).map(([key, label]) => (
                  <Select.Option key={key} value={key}>{label}</Select.Option>
                ))}
              </Select>
              <p className="mt-1 text-[11px] text-slate-500">이 메시지를 받을 사용자 그룹을 선택하세요.</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InAppMessagingManager;
